import { usePage } from '@inertiajs/react'
import { Badge } from '@/shared/ui/badge'
import { t } from '@/shared/lib/i18n'
import { useBreadcrumbs } from '@/shared/lib/breadcrumbs-context'
import { cn } from '@/shared/lib/utils'
import type { SharedProps } from '@/shared/types/SharedProps'
import type WebAdminSubscriptionUsage from '@/shared/types/web/WebAdminSubscriptionUsage'

interface AdminSubscriptionPageProps {
  subscriptionUsage: WebAdminSubscriptionUsage
}

type PageProps = SharedProps & AdminSubscriptionPageProps

const capPercent = (value: number) => Math.min(100, Math.max(0, value))

const UsageRow = (props: {
  title: string
  usedLabel: string
  used: number
  limitLabel: string
  limitDisplay: string
  remainingLabel: string
  remainingDisplay: string
  percent: number | null
  footnote?: string
  unlimitedBadge?: boolean
}) => {
  const { title, used, limitDisplay, remainingDisplay, percent, footnote, unlimitedBadge } = props
  return (
    <div className="space-y-4 rounded-lg border bg-card p-5 text-card-foreground shadow-sm">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-lg font-semibold tracking-tight">{title}</h2>
        {unlimitedBadge && (
          <Badge variant="secondary" className="shrink-0">
            {t('admin.subscription.unlimited')}
          </Badge>
        )}
      </div>
      <dl className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          <dt className="text-xs font-medium text-muted-foreground uppercase">{props.usedLabel}</dt>
          <dd className="text-2xl font-semibold tabular-nums">{used}</dd>
        </div>
        <div>
          <dt className="text-xs font-medium text-muted-foreground uppercase">
            {props.limitLabel}
          </dt>
          <dd className="text-2xl font-semibold tabular-nums">{limitDisplay}</dd>
        </div>
        <div>
          <dt className="text-xs font-medium text-muted-foreground uppercase">
            {props.remainingLabel}
          </dt>
          <dd className="text-2xl font-semibold tabular-nums">{remainingDisplay}</dd>
        </div>
      </dl>
      {percent != null && (
        <div className="space-y-2">
          <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
            <div
              className={cn(
                'h-full rounded-full transition-[width] duration-300',
                capPercent(percent) >= 90 ? 'bg-destructive' : 'bg-primary',
              )}
              style={{ width: `${capPercent(percent)}%` }}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            {t('admin.subscription.utilizationLabel')}: {capPercent(percent)}%
          </p>
        </div>
      )}
      {footnote && <p className="text-sm text-muted-foreground">{footnote}</p>}
    </div>
  )
}

const AdminSubscriptionIndex = () => {
  const { subscriptionUsage } = usePage<PageProps>().props

  useBreadcrumbs([{ label: t('nav.administration') }, { label: t('nav.subscription') }])

  const userLimitUnlimited = subscriptionUsage.userLimit == null
  const usersLimitDisplay = userLimitUnlimited
    ? t('admin.subscription.unlimited')
    : String(subscriptionUsage.userLimit)
  const usersRemainingDisplay =
    subscriptionUsage.userRemaining == null
      ? t('admin.subscription.dash')
      : String(subscriptionUsage.userRemaining)
  const siteLimitUnlimited = subscriptionUsage.siteLimit == null
  const sitesLimitDisplay = siteLimitUnlimited
    ? t('admin.subscription.unlimited')
    : String(subscriptionUsage.siteLimit)
  const sitesRemainingDisplay =
    subscriptionUsage.siteRemaining == null
      ? t('admin.subscription.dash')
      : String(subscriptionUsage.siteRemaining)

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">{t('admin.subscription.title')}</h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          {t('admin.subscription.subtitle')}
        </p>
      </div>

      <div className="space-y-6">
        <UsageRow
          title={t('admin.subscription.usersTitle')}
          usedLabel={t('admin.subscription.inUse')}
          used={subscriptionUsage.userUsed}
          limitLabel={t('admin.subscription.allowedUsers')}
          limitDisplay={usersLimitDisplay}
          remainingLabel={t('admin.subscription.remaining')}
          remainingDisplay={usersRemainingDisplay}
          percent={subscriptionUsage.userUtilizationPercent ?? null}
          unlimitedBadge={userLimitUnlimited}
          footnote={
            subscriptionUsage.userLimit === 0
              ? t('admin.subscription.userLimitZeroNote')
              : undefined
          }
        />

        <UsageRow
          title={t('admin.subscription.sitesTitle')}
          usedLabel={t('admin.subscription.inUse')}
          used={subscriptionUsage.siteUsed}
          limitLabel={t('admin.subscription.allowedSites')}
          limitDisplay={sitesLimitDisplay}
          remainingLabel={t('admin.subscription.remaining')}
          remainingDisplay={sitesRemainingDisplay}
          percent={subscriptionUsage.siteUtilizationPercent ?? null}
          unlimitedBadge={siteLimitUnlimited}
        />
      </div>
    </div>
  )
}

export default AdminSubscriptionIndex
